import { c as _c } from "react/compiler-runtime";
import { IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import type { VaultKind } from '@onefootprint/request-types';
import { Form, IconButton, InlineAlert, LinkButton } from '@onefootprint/ui';
import snakeCase from 'lodash/snakeCase';
import { useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useMobileLayout from '@/hooks/use-mobile-layout';
import ColorComponent from '../color-component';
export type CustomStatusFormData = {
  displayName: string;
  slug: string;
  entityKind: VaultKind;
  entries: {
    displayName: string;
    slug: string;
    hexCode?: string;
  }[];
};
type CustomStatusFormProps = {
  kind: 'add' | 'edit';
  entityKind: VaultKind;
  defaultValues?: CustomStatusFormData;
  onSubmit: (data: CustomStatusFormData) => Promise<void>;
  setDirty: (isDirty: boolean) => void;
};
const CustomStatusForm = t0 => {
  const $ = _c(115);
  const {
    kind,
    entityKind,
    defaultValues,
    onSubmit,
    setDirty
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "pages.statuses.customStatusAddEditDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("settings", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [manuallyEditedSlugs, setManuallyEditedSlugs] = useState(t2);
  const [statusSlugManuallyEdited, setStatusSlugManuallyEdited] = useState(false);
  const portalContainerRef = useRef(null);
  const isMobile = useMobileLayout();
  let t3;
  if ($[2] !== defaultValues || $[3] !== entityKind) {
    t3 = defaultValues ?? {
      displayName: "",
      slug: "",
      entityKind,
      entries: [{
        displayName: "",
        slug: "",
        hexCode: "#000000"
      }]
    };
    $[2] = defaultValues;
    $[3] = entityKind;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = {
      defaultValues: t3
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const form = useForm(t4);
  let t5;
  if ($[7] !== t) {
    t5 = {
      validate: value => {
        if (value.length < 1) {
          return t(_temp);
        }
        return true;
      }
    };
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== form.control || $[10] !== t5) {
    t6 = {
      control: form.control,
      name: "entries",
      rules: t5
    };
    $[9] = form.control;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    fields,
    append,
    remove
  } = useFieldArray(t6);
  let t7;
  if ($[12] !== form.formState.isDirty || $[13] !== setDirty) {
    t7 = () => {
      setDirty(form.formState.isDirty);
    };
    $[12] = form.formState.isDirty;
    $[13] = setDirty;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== form.formState.isDirty) {
    t8 = [form.formState.isDirty];
    $[15] = form.formState.isDirty;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  useEffect(t7, t8);
  let t9;
  if ($[17] !== defaultValues?.entries) {
    t9 = index => index >= (defaultValues?.entries.length ?? 0);
    $[17] = defaultValues?.entries;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const isNewEntry = t9;
  let t10;
  if ($[19] !== form || $[20] !== isNewEntry || $[21] !== manuallyEditedSlugs) {
    t10 = (index_0, fieldId, value_0) => {
      if (!isNewEntry(index_0) || manuallyEditedSlugs.has(fieldId)) {
        return;
      }
      form.setValue(`entries.${index_0}.slug`, snakeCase(value_0));
    };
    $[19] = form;
    $[20] = isNewEntry;
    $[21] = manuallyEditedSlugs;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const handleEntryDisplayNameChange = t10;
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = fieldId_0 => {
      setManuallyEditedSlugs(prev => new Set(prev).add(fieldId_0));
    };
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const handleEntrySlugChange = t11;
  let t12;
  if ($[24] !== form || $[25] !== kind || $[26] !== statusSlugManuallyEdited) {
    t12 = value_1 => {
      if (kind === "edit" || statusSlugManuallyEdited) {
        return;
      }
      form.setValue("slug", snakeCase(value_1));
    };
    $[24] = form;
    $[25] = kind;
    $[26] = statusSlugManuallyEdited;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const handleStatusDisplayNameChange = t12;
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setStatusSlugManuallyEdited(true);
    };
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const handleStatusSlugChange = t13;
  let t14;
  if ($[29] !== form || $[30] !== onSubmit) {
    t14 = form.handleSubmit(onSubmit);
    $[29] = form;
    $[30] = onSubmit;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== kind || $[33] !== t) {
    t15 = kind === "add" && <InlineAlert variant="neutral" className="p-3 rounded-default"><p className="text-body-2 text-primary">{t(_temp2)}</p></InlineAlert>;
    $[32] = kind;
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t) {
    t16 = t(_temp3);
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t) {
    t17 = t(_temp4);
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t) {
    t18 = t(_temp5);
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t18) {
    t19 = {
      required: t18
    };
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== handleStatusDisplayNameChange) {
    t20 = e => handleStatusDisplayNameChange(e.target.value);
    $[43] = handleStatusDisplayNameChange;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t16 || $[46] !== t17 || $[47] !== t19 || $[48] !== t20) {
    t21 = <Form.Input name="displayName" label={t16} placeholder={t17} size="compact" rules={t19} onChange={t20} />;
    $[45] = t16;
    $[46] = t17;
    $[47] = t19;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t) {
    t22 = t(_temp6);
    $[50] = t;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t) {
    t23 = t(_temp7);
    $[52] = t;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t) {
    t24 = t(_temp8);
    $[54] = t;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = /^\S*$/;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t) {
    t26 = t(_temp9);
    $[57] = t;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t26) {
    t27 = {
      value: t25,
      message: t26
    };
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t24 || $[62] !== t27) {
    t28 = {
      required: t24,
      pattern: t27
    };
    $[61] = t24;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  const t29 = kind === "edit";
  let t30;
  if ($[64] !== t22 || $[65] !== t23 || $[66] !== t28 || $[67] !== t29) {
    t30 = <Form.Input name="slug" label={t22} placeholder={t23} size="compact" rules={t28} disabled={t29} onChange={handleStatusSlugChange} autoCapitalize="none" autoCorrect="off" />;
    $[64] = t22;
    $[65] = t23;
    $[66] = t28;
    $[67] = t29;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t21 || $[70] !== t30) {
    t31 = <div className="flex flex-col gap-4 md:flex-row md:gap-2">{t21}{t30}</div>;
    $[69] = t21;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t) {
    t32 = t(_temp10);
    $[72] = t;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  let t33;
  if ($[74] !== t32) {
    t33 = <div className="flex-1"><p className="text-label-2 ml-10">{t32}</p></div>;
    $[74] = t32;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  let t34;
  if ($[76] !== t) {
    t34 = t(_temp11);
    $[76] = t;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== t34) {
    t35 = <p className="text-label-2 flex-1">{t34}</p>;
    $[78] = t34;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] !== t33 || $[81] !== t35) {
    t36 = <div className="hidden gap-2 md:flex">{t33}{t35}</div>;
    $[80] = t33;
    $[81] = t35;
    $[82] = t36;
  } else {
    t36 = $[82];
  }
  let t37;
  if ($[83] !== fields || $[84] !== form.control || $[85] !== handleEntryDisplayNameChange || $[86] !== isMobile || $[87] !== isNewEntry || $[88] !== remove || $[89] !== t) {
    t37 = fields.map((field, index_1) => <div key={field.id} className="flex flex-col gap-2 md:flex-row md:items-start"><div className="flex gap-2 items-end md:items-start md:flex-1"><Controller name={`entries.${index_1}.hexCode`} control={form.control} render={t38 => {
          const {
            field: field_0
          } = t38;
          return <ColorComponent value={field_0.value} onChange={field_0.onChange} portalContainerRef={portalContainerRef} />;
        }} /><Form.Input name={`entries.${index_1}.displayName`} label={isMobile ? t(_temp12) : undefined} aria-label={t(_temp13)} placeholder={t(_temp14)} size="compact" rules={{
          required: t(_temp15)
        }} onChange={e_0 => handleEntryDisplayNameChange(index_1, field.id, e_0.target.value)} /></div><div className="flex gap-2 items-end pl-10 md:items-start md:flex-1 md:pl-0"><div className="flex-1"><Form.Input name={`entries.${index_1}.slug`} label={isMobile ? t(_temp16) : undefined} aria-label={t(_temp17)} placeholder={t(_temp18)} size="compact" rules={{
            required: t(_temp19),
            pattern: {
              value: /^\S*$/,
              message: t(_temp20)
            }
          }} disabled={!isNewEntry(index_1)} onChange={() => handleEntrySlugChange(field.id)} autoCapitalize="none" autoCorrect="off" /></div>{isNewEntry(index_1) && <IconButton icon={IcoTrash16} destructive={true} aria-label={t(_temp21)} onClick={() => remove(index_1)} />}</div></div>);
    $[83] = fields;
    $[84] = form.control;
    $[85] = handleEntryDisplayNameChange;
    $[86] = isMobile;
    $[87] = isNewEntry;
    $[88] = remove;
    $[89] = t;
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== form.formState.errors.entries) {
    t38 = form.formState.errors.entries?.root?.message && <p className="text-body-2 text-error">{form.formState.errors.entries.root.message}</p>;
    $[91] = form.formState.errors.entries;
    $[92] = t38;
  } else {
    t38 = $[92];
  }
  let t39;
  if ($[93] !== t36 || $[94] !== t37 || $[95] !== t38) {
    t39 = <div className="flex flex-col gap-4 md:gap-2">{t36}{t37}{t38}</div>;
    $[93] = t36;
    $[94] = t37;
    $[95] = t38;
    $[96] = t39;
  } else {
    t39 = $[96];
  }
  let t40;
  if ($[97] !== append) {
    t40 = () => append({
      displayName: "",
      slug: "",
      hexCode: "#000000"
    });
    $[97] = append;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  let t41;
  if ($[99] !== t) {
    t41 = t(_temp22);
    $[99] = t;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  let t42;
  if ($[101] !== t40 || $[102] !== t41) {
    t42 = <LinkButton iconComponent={IcoPlusSmall16} iconPosition="left" onClick={t40}>{t41}</LinkButton>;
    $[101] = t40;
    $[102] = t41;
    $[103] = t42;
  } else {
    t42 = $[103];
  }
  let t43;
  if ($[104] !== t39 || $[105] !== t42) {
    t43 = <div className="flex flex-col gap-4">{t39}{t42}</div>;
    $[104] = t39;
    $[105] = t42;
    $[106] = t43;
  } else {
    t43 = $[106];
  }
  let t44;
  if ($[107] !== t14 || $[108] !== t15 || $[109] !== t31 || $[110] !== t43) {
    t44 = <form ref={portalContainerRef} className="flex flex-col gap-6" id="custom-status-add-edit-form" onSubmit={t14}>{t15}{t31}{t43}</form>;
    $[107] = t14;
    $[108] = t15;
    $[109] = t31;
    $[110] = t43;
    $[111] = t44;
  } else {
    t44 = $[111];
  }
  let t45;
  if ($[112] !== form || $[113] !== t44) {
    t45 = <FormProvider {...form}>{t44}</FormProvider>;
    $[112] = form;
    $[113] = t44;
    $[114] = t45;
  } else {
    t45 = $[114];
  }
  return t45;
};
export default CustomStatusForm;
function _temp($) {
  return $.minEntries;
}
function _temp2($_0) {
  return $_0.slugDescription;
}
function _temp3($_1) {
  return $_1.form.category.label;
}
function _temp4($_2) {
  return $_2.form.category.placeholder;
}
function _temp5($_3) {
  return $_3.form.category.required;
}
function _temp6($_4) {
  return $_4.form.slug.label;
}
function _temp7($_5) {
  return $_5.form.slug.placeholder;
}
function _temp8($_6) {
  return $_6.form.slug.required;
}
function _temp9($_7) {
  return $_7.form.slug.noSpaces;
}
function _temp10($_8) {
  return $_8.form.entries.statusLabel;
}
function _temp11($_9) {
  return $_9.form.entries.slugLabel;
}
function _temp12($_10) {
  return $_10.form.entries.statusLabel;
}
function _temp13($_11) {
  return $_11.form.entries.statusLabel;
}
function _temp14($_12) {
  return $_12.form.entries.statusPlaceholder;
}
function _temp15($_13) {
  return $_13.form.entries.statusRequired;
}
function _temp16($_14) {
  return $_14.form.entries.slugLabel;
}
function _temp17($_15) {
  return $_15.form.entries.slugLabel;
}
function _temp18($_16) {
  return $_16.form.entries.slugPlaceholder;
}
function _temp19($_17) {
  return $_17.form.entries.slugRequired;
}
function _temp20($_18) {
  return $_18.form.entries.noSpaces;
}
function _temp21($_19) {
  return $_19.form.entries.removeAriaLabel;
}
function _temp22($_20) {
  return $_20.form.entries.addStatus;
}